import React, { useState, useRef, useEffect } from 'react';
import { countries } from '../DataCapturer/constants';

// Country input with type-ahead suggestions.
// Suggestions only render once the user has typed >= 3 characters,
// so the dropdown never appears on focus or while empty.

const MIN_CHARS = 3;
const MAX_SUGGESTIONS = 8;

function CountryAutocomplete({
  value,
  onChange,
  placeholder = 'Start typing a country…',
  required = false,
  id,
  name,
  disabled
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const text = value || '';
  const lc   = text.trim().toLowerCase();
  const suggestions = lc.length >= MIN_CHARS
    ? countries
        .filter(c => c.toLowerCase().includes(lc))
        .slice(0, MAX_SUGGESTIONS)
    : [];

  const handleChange = (e) => {
    onChange(e.target.value);
    setHighlight(-1);
    // Only open the panel once the threshold is met
    setOpen(e.target.value.trim().length >= MIN_CHARS);
  };

  const pick = (country) => {
    onChange(country);
    setOpen(false);
    setHighlight(-1);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(suggestions.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      if (highlight >= 0) {
        e.preventDefault();
        pick(suggestions[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        type="text"
        id={id}
        name={name}
        value={text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        // Don't auto-open on focus — only when typing reaches MIN_CHARS
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <ul
          style={{
            position: 'absolute',
            zIndex: 10,
            top: '100%',
            left: 0,
            right: 0,
            margin: 0,
            padding: 0,
            listStyle: 'none',
            background: 'var(--card-bg, #fff)',
            border: '1px solid var(--border-color, #d0d7dc)',
            borderRadius: 6,
            boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
            maxHeight: 240,
            overflowY: 'auto'
          }}
        >
          {suggestions.map((c, idx) => (
            <li
              key={c}
              onMouseDown={(e) => { e.preventDefault(); pick(c); }}
              onMouseEnter={() => setHighlight(idx)}
              style={{
                padding: '0.45rem 0.75rem',
                cursor: 'pointer',
                background: idx === highlight ? 'var(--accent-light, #e0f2f1)' : 'transparent',
                color: 'var(--text-primary, #1a1a1a)',
                fontSize: '0.9rem'
              }}
            >
              {c}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default CountryAutocomplete;
